// Libraries
import * as RouterActions from 'connected-react-router';
import { Formik, Field } from 'formik';
import Immutable from 'immutable';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

// Material UI
import { makeStyles } from '@material-ui/core/styles';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Fab from '@material-ui/core/Fab';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import LogoutIcon from '@material-ui/icons/ExitToApp';
import CheckIcon from '@material-ui/icons/Check';
import UndoIcon from '@material-ui/icons/Undo';
import Autocomplete from '@material-ui/lab/Autocomplete';

import ErrorModal from '../components/error-modal';
import axios from 'global/axios-instance';
import * as GlobalActions from '../../global/actions/global-actions';

const useStyles = makeStyles(theme => ({
    container: {
        maxWidth: '600px',
        width: '100%',
        margin: '1rem auto',
        padding: theme.spacing(2),
        textAlign: 'center',
        position: 'relative'
    },
    h2: {
        width: '100%',
        paddingLeft: theme.spacing(2),
        textAlign: 'left'
    },
    formContainer: {
        display: 'flex',
        flexWrap: 'wrap'
    },
    formControl: {
        margin: theme.spacing(2),
        width: '100%',
        textAlign: 'left'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        margin: '1rem'
    },
    logoutButton: {
        position: 'absolute',
        top: '1rem',
        right: '1rem'
    },
    countryChip: {
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(1)
    },
    countryFilter: {
        padding: theme.spacing(1)
    },
    countryAutoComplete: {
        width: '100%'
    },
    formLabel: {
        marginBottom: theme.spacing(1)
    }
}));

const AccountRequest = () => {
    const dispatch = useDispatch();
    const push = (path) => dispatch(RouterActions.push(path));
    const logout = () => dispatch(GlobalActions.logout());
    const updateSession = (payload) => dispatch(GlobalActions.updateSession(payload));

    const classes = useStyles();
    const currentUser = useSelector(state => state.session.user);

    const [countries, setCountries] = useState(Immutable.List());
    const [regions, setRegions] = useState(Immutable.List());
    const [accessType, setAccessType] = useState(null);
    const [errorResponse, setErrorResponse] = useState(null);
    const [submitError, setSubmitError] = useState(false);

    const schema = Yup.object().shape({
        employedCountry: Yup.string().required('Please select a country'),
        residenceCountry: Yup.string().required('Please select a country'),
        accessCountries: Yup.array().of(Yup.string()).nullable(),
        accessRegions: Yup.array().of(Yup.string()).nullable()
    }).test(
        'access-countries-xor-access-regions',
        'Select either one or more countries OR one or more regions (not both).',
        (value) => {
            const hasCountries = Array.isArray(value?.accessCountries) && value.accessCountries.length > 0;
            const hasRegions = Array.isArray(value?.accessRegions) && value.accessRegions.length > 0;

            return (hasCountries || hasRegions) && !(hasCountries && hasRegions);
        }
    );

    useEffect(() => {
        const load = async () => {
            const countriesResponse = await axios.get('/lookups/countries');
            const regionsResponse = await axios.get('/lookups/regions');

            setCountries(Immutable.fromJS(countriesResponse.data));
            setRegions(Immutable.fromJS(regionsResponse.data));
        };

        load();
    }, []);

    const getCountryByCode = (countryCode) => {
        return _.isEmpty(countryCode) ? '' : countries.find(c => c.get('countryCode') === countryCode);
    };

    const onSubmit = async (values) => {
        try {
            const result = await axios.post('/user-creation-requests', values);
            const { user } = result.data;

            await updateSession({ user });
            return push('/access-pending');
        } catch (err) {
            setSubmitError(true);
            setErrorResponse(err.response);
        }
    };

    const onLogout = () => {
        logout();
        window.location.href = '/';
    };

    const initialValues = {
        userId: currentUser.userId,
        employedCountry: '',
        residenceCountry: '',
        accessCountries: [],
        accessRegions: []
    };

    return (<Paper className={classes.container}>
        <h2 className={classes.h2}>Please answer these questions to continue logging in.</h2>
        <Formik
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={(values, { setSubmitting }) => {
                onSubmit(values)
                    .then(() => setSubmitting(false));
            }}>
            {({
                values,
                errors,
                touched,
                handleBlur,
                handleSubmit,
                setFieldValue,
                isSubmitting
                /* and other goodies */
            }) => {
                const renderCountryTextField = (params, fieldName, label) => {
                    return (
                        <TextField
                            {...params}
                            className={classes.textField}
                            label={label}
                            error={errors[fieldName] && touched[fieldName]} />
                    );
                };
                const renderResidenceCountryInput = (params) => {
                    return renderCountryTextField(params, 'residenceCountry', 'Residence Country');
                };
                const renderEmployedCountryInput = (params) => {
                    return renderCountryTextField(params, 'employedCountry', 'Employed Country');
                };
                const renderAccessCountriesInput = (params) => {
                    return <TextField {...params} label='Search countries' variant='outlined' size='small' />;
                };
                return (
                    <form onSubmit={handleSubmit}>
                        <div className={classes.formContainer}>
                            <input type='hidden' name='userId' value={values.userId} />
                            <FormControl component='fieldset' className={classes.formControl}>
                                <FormLabel component='legend'>In which country do you live?</FormLabel>
                                <Autocomplete
                                    name='residenceCountry'
                                    options={countries.toArray()}
                                    getOptionLabel={(option) => _.isEmpty(option) ? '' : option.get('countryName')}
                                    renderInput={params => renderResidenceCountryInput(params)}
                                    value={getCountryByCode(values.residenceCountry)}
                                    onBlur={handleBlur}
                                    onChange={(ev, v) => {
                                        setFieldValue('residenceCountry', (v && v.get('countryCode')) || '');
                                    }} />
                                {errors.residenceCountry && touched.residenceCountry &&
                                    <FormHelperText error>{errors.residenceCountry}</FormHelperText>
                                }
                            </FormControl>
                            {!_.isEmpty(values.residenceCountry) && (
                                <FormControl component='fieldset' className={classes.formControl}>
                                    <FormLabel component='legend'>In which country are you employed?</FormLabel>
                                    <Autocomplete
                                        name='employedCountry'
                                        options={countries.toArray()}
                                        getOptionLabel={(option) => _.isEmpty(option) ? '' : option.get('countryName')}
                                        renderInput={params => renderEmployedCountryInput(params)}
                                        value={getCountryByCode(values.employedCountry)}
                                        onBlur={handleBlur}
                                        onChange={(ev, v) => {
                                            setFieldValue('employedCountry', (v && v.get('countryCode')) || '');
                                        }} />
                                    {errors.employedCountry && touched.employedCountry &&
                                        <FormHelperText error>{errors.employedCountry}</FormHelperText>
                                    }
                                </FormControl>
                            )}
                            {!_.isEmpty(values.employedCountry) && _.isEmpty(accessType) && (
                                <FormControl component='fieldset' className={classes.formControl}>
                                    <FormLabel component='legend'>
                                        Do you need access for specific countries or entire regions?<br/>
                                        (If you are unsure, choose “Specific Countries”)
                                    </FormLabel>
                                    <div>
                                        <Fab
                                            size='medium'
                                            variant='extended'
                                            color={accessType === 'countries' ? 'primary' : 'default'}
                                            onClick={() => setAccessType('countries')}>
                                            Specific Countries
                                        </Fab>
                                        <Fab
                                            size='medium'
                                            variant='extended'
                                            color={accessType === 'regions' ? 'primary' : 'default'}
                                            onClick={() => setAccessType('regions')}>
                                            Entire Regions
                                        </Fab>
                                    </div>
                                </FormControl>
                            )}
                            {accessType === 'countries' && (
                                <FormControl component='fieldset' className={classes.formControl}>
                                    {values.accessCountries.length > 0 && (
                                        <div className={classes.countryChips}>
                                            {values.accessCountries.map((countryCode) => (
                                                <Chip
                                                    key={countryCode}
                                                    className={classes.countryChip}
                                                    label={countries.find(c => c.get('countryCode') === countryCode)
                                                        .get('countryName')}
                                                    onDelete={() => {
                                                        setFieldValue(
                                                            'accessCountries',
                                                            values.accessCountries.filter(c => c !== countryCode)
                                                        );
                                                    }} />
                                            ))}
                                        </div>
                                    )}
                                    <FormLabel component='legend' className={classes.formLabel}>
                                        To which country/countries do you need access?
                                    </FormLabel>
                                    <Autocomplete
                                        key={values.accessCountries.length}
                                        onChange={(e, value) => {
                                            if (!_.isNil(value)) {
                                                setFieldValue(
                                                    'accessCountries',
                                                    [...values.accessCountries, value.countryCode]
                                                );
                                            }
                                        }}
                                        className={classes.countryAutoComplete}
                                        options={countries.toJS()}
                                        getOptionDisabled={(option) => {
                                            return values.accessCountries.includes(option.countryCode);
                                        }}
                                        getOptionLabel={(option) => option.countryName}
                                        renderInput={params => renderAccessCountriesInput(params)} />
                                    {errors.accessCountries && touched.accessCountries &&
                                        <FormHelperText error>{errors.accessCountries}</FormHelperText>
                                    }
                                </FormControl>
                            )}
                            {accessType === 'regions' && (
                                <FormControl component='fieldset' className={classes.formControl}>
                                    <FormLabel component='legend'>To which region(s) do you need access?</FormLabel>
                                    <FormGroup>
                                        {regions.sortBy(r => r.get('awsRegion')).map(r => (
                                            <FormControlLabel
                                                key={r.get('awsRegion')}
                                                control={
                                                    <Field
                                                        as={Checkbox}
                                                        type='checkbox'
                                                        name='accessRegions'
                                                        value={r.get('awsRegion')}
                                                    />
                                                }
                                                label={r.get('regionName')}
                                            />
                                        ))}
                                    </FormGroup>
                                    {errors.accessRegions && touched.accessRegions &&
                                        <FormHelperText error>{errors.accessRegions}</FormHelperText>
                                    }
                                </FormControl>
                            )}
                        </div>
                        <div className={classes.buttonContainer} >
                            <Button
                                variant='text'
                                startIcon={<LogoutIcon />}
                                onClick={onLogout}
                                disabled={isSubmitting}>
                                Cancel and logout
                            </Button>
                            {!_.isNil(accessType) && (
                                <Button
                                    variant='outlined'
                                    color='secondary'
                                    startIcon={<UndoIcon />}
                                    onClick={() => {
                                        setAccessType(null);
                                        setFieldValue('accessCountries', []);
                                        setFieldValue('accessRegions', []);
                                    }}
                                    disabled={isSubmitting}>
                                    Go Back
                                </Button>
                            )}
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<CheckIcon />}
                                onClick={handleSubmit}
                                disabled={isSubmitting}>
                                Submit
                            </Button>
                        </div>
                    </form>
                );
            }}
        </Formik>
        <ErrorModal
            errorModalOpen={submitError}
            errorResponse={errorResponse}
            onCloseModal={() => setSubmitError(false)} />
    </Paper>
    );
};

export default AccountRequest;
